import { supabase } from "@/lib/supabase";
import type { GlobalSearchGroup, GlobalSearchResult } from "@/types/global-search";

const RESULT_LIMIT = 8;

function searchValue(value: string) {
  return value.trim().replace(/[%_,().]/g, " ").replace(/\s+/g, " ");
}

function queryTerm(value: string) {
  return `%${searchValue(value)}%`;
}

function text(...values: Array<string | number | null | undefined>) {
  return values.filter((value) => value !== null && value !== undefined && String(value).trim()).join(" • ");
}

function group(category: GlobalSearchGroup["category"], label: string, results: GlobalSearchResult[]): GlobalSearchGroup {
  return { category, label, results };
}

async function searchEquipamentos(term: string) {
  const { data, error } = await supabase
    .from("vw_equipamentos_detalhados")
    .select("id,nome,codigo_interno,patrimonio,numero_serie,service_tag,endereco_ip,endereco_mac,status,categoria_nome,filial_nome")
    .or(`nome.ilike.${term},codigo_interno.ilike.${term},patrimonio.ilike.${term},numero_serie.ilike.${term},service_tag.ilike.${term},endereco_ip.ilike.${term},endereco_mac.ilike.${term}`)
    .limit(RESULT_LIMIT);
  if (error) throw error;
  return group("equipamentos", "Equipamentos", (data ?? []).map((item) => ({
    id: item.id,
    category: "equipamentos",
    title: item.nome,
    description: text(item.codigo_interno, item.patrimonio && `Patrimônio ${item.patrimonio}`, item.categoria_nome, item.filial_nome),
    status: item.status,
    href: `/equipamentos/${item.id}`,
  })));
}

async function searchColaboradores(term: string) {
  const { data, error } = await supabase
    .from("colaboradores")
    .select("id,nome,matricula,email,cargo,status")
    .or(`nome.ilike.${term},matricula.ilike.${term},email.ilike.${term},cargo.ilike.${term}`)
    .is("excluido_em", null)
    .limit(RESULT_LIMIT);
  if (error) throw error;
  return group("colaboradores", "Colaboradores", (data ?? []).map((item) => ({
    id: item.id,
    category: "colaboradores",
    title: item.nome,
    description: text(item.matricula && `Matrícula ${item.matricula}`, item.cargo, item.email),
    status: item.status,
    href: `/colaboradores?busca=${encodeURIComponent(item.nome)}`,
  })));
}

async function searchSuprimentos(term: string) {
  const { data, error } = await supabase
    .from("vw_suprimentos_detalhados")
    .select("id,nome,codigo,modelo,status,nivel_estoque,quantidade_atual,unidade_medida,filial_nome")
    .or(`nome.ilike.${term},codigo.ilike.${term},modelo.ilike.${term},codigo_barras.ilike.${term}`)
    .limit(RESULT_LIMIT);
  if (error) throw error;
  return group("suprimentos", "Suprimentos", (data ?? []).map((item) => ({
    id: item.id,
    category: "suprimentos",
    title: item.nome,
    description: text(item.codigo, item.modelo, `${item.quantidade_atual} ${item.unidade_medida}`, item.filial_nome),
    status: item.nivel_estoque || item.status,
    href: `/suprimentos/${item.id}`,
  })));
}

async function searchLicencas(term: string) {
  const { data, error } = await supabase
    .from("licencas")
    .select("id,nome,fabricante,tipo,data_vencimento,ativo")
    .or(`nome.ilike.${term},fabricante.ilike.${term},tipo.ilike.${term},chave_licenca.ilike.${term}`)
    .is("excluido_em", null)
    .limit(RESULT_LIMIT);
  if (error) throw error;
  return group("licencas", "Licenças", (data ?? []).map((item) => ({
    id: item.id,
    category: "licencas",
    title: item.nome,
    description: text(item.fabricante, item.tipo, item.data_vencimento && `Vence em ${item.data_vencimento}`),
    status: item.ativo ? "ativa" : "inativa",
    href: `/licencas?busca=${encodeURIComponent(item.nome)}`,
  })));
}

async function searchManutencoes(term: string) {
  const { data, error } = await supabase
    .from("manutencoes")
    .select("id,numero,tipo,defeito_relatado,numero_ordem_servico,status")
    .or(`numero.ilike.${term},tipo.ilike.${term},defeito_relatado.ilike.${term},numero_ordem_servico.ilike.${term}`)
    .is("excluido_em", null)
    .limit(RESULT_LIMIT);
  if (error) throw error;
  return group("manutencoes", "Manutenções", (data ?? []).map((item) => ({
    id: item.id,
    category: "manutencoes",
    title: `${item.numero} — ${item.tipo}`,
    description: text(item.numero_ordem_servico && `OS ${item.numero_ordem_servico}`, item.defeito_relatado),
    status: item.status,
    href: `/manutencoes?busca=${encodeURIComponent(item.numero)}`,
  })));
}

export const globalSearchService = {
  async search(value: string): Promise<GlobalSearchGroup[]> {
    const clean = searchValue(value);
    if (clean.length < 2) return [];
    const term = queryTerm(clean);
    const operations = [
      searchEquipamentos(term),
      searchColaboradores(term),
      searchSuprimentos(term),
      searchLicencas(term),
      searchManutencoes(term),
    ];
    const settled = await Promise.allSettled(operations);
    const successful = settled
      .filter((item): item is PromiseFulfilledResult<GlobalSearchGroup> => item.status === "fulfilled")
      .map((item) => item.value)
      .filter((item) => item.results.length > 0);
    if (!successful.length && settled.every((item) => item.status === "rejected")) {
      throw new Error("Não foi possível consultar os módulos agora.");
    }
    return successful;
  },
};
